// M014 Phase 14.2: shared Services + Deploys cards. Rendered by both
// the public standalone /status page (StatusPage) and the in-project
// Status tab (StatusTab) so both surfaces stay in sync automatically.
// Poll cadence, radial-countdown spinner, auto-reload-on-frontend-SHA
// behaviour all live inside this component.

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "http://localhost:3001";
const SPACETIMEDB_HOST =
  (import.meta.env.VITE_SPACETIMEDB_HOST as string | undefined) ??
  "wss://maincloud.spacetimedb.com";
const SPACETIMEDB_DB =
  (import.meta.env.VITE_SPACETIMEDB_DB as string | undefined) ??
  "waytide-staging";

const POLL_INTERVAL = 5_000;
const SPINNER_MS = 2_000;

interface Check {
  label: string;
  status: "checking" | "ok" | "error";
  detail: string;
  responseTime?: number;
}

interface DeployInfo {
  repo: string;
  status: "checking" | "success" | "failure" | "in_progress" | "unknown";
  sha: string;
  time: string;
  url: string;
}

const REPO_NAMES = ["Backend", "Frontend", "DB Module", "Orchestrator"];

function timeAgo(dateStr: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  if (isNaN(diff)) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Coral 2026-04-23 standardisation pass: inside tab content, panels
// use the same recipe across every tab — rounded-md + border-edge +
// bg-surface-raised, no shadow, no backdrop-blur. Glass surfaces are
// reserved for chrome (tab bar, composer), not panels. `rounded-panel`
// + shadow + glass are still fine on trading-card-style surfaces like
// PersonaCard where the elevated treatment pays for itself.
const cardClass =
  "rounded-md border border-edge bg-surface-raised overflow-hidden";
const labelClass = "text-muted text-xs font-medium";
const dimClass = "text-muted text-xs font-medium";
const monoClass = "text-faint font-code text-xs";

// Coral 2026-04-23: no green "success" tint on the status page. OK /
// Connected / Deployed are the baseline state — the label already
// carries the claim; painting it green adds no information and
// competes visually with the accent. Only failures should yell,
// which makes the red draw the eye when there's actually a problem.
function statusColor(s: string) {
  if (s === "error" || s === "failure") return "text-danger";
  if (s === "in_progress") return "text-muted animate-pulse";
  return "text-muted";
}

interface Props {
  /** Auto-reload the page when a new Frontend build SHA lands. On
   * for the public status page (operators expect the visible SHA to
   * track head); off for the in-project tab (reload there drops the
   * user back to the Chat tab and that's disruptive). */
  reloadOnFrontendSha?: boolean;
}

export function StatusCards({ reloadOnFrontendSha = false }: Props) {
  const [checks, setChecks] = useState<Check[]>([
    { label: "Backend API", status: "checking", detail: "Connecting…" },
    { label: "SpacetimeDB", status: "checking", detail: "Connecting…" },
  ]);

  const [deploys, setDeploys] = useState<DeployInfo[]>(
    REPO_NAMES.map((name) => ({
      repo: name,
      status: "checking",
      sha: "",
      time: "",
      url: "",
    })),
  );

  function updateCheck(index: number, update: Partial<Check>) {
    setChecks((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...update } : c)),
    );
  }
  function updateDeploy(index: number, update: Partial<DeployInfo>) {
    setDeploys((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...update } : d)),
    );
  }

  const frontendShaRef = useRef<string | null>(null);

  useEffect(() => {
    if (!reloadOnFrontendSha) return;
    const frontendDeploy = deploys[1];
    if (!frontendDeploy.sha || frontendDeploy.status === "checking") return;
    if (frontendShaRef.current === null) {
      frontendShaRef.current = frontendDeploy.sha;
    } else if (
      frontendDeploy.sha !== frontendShaRef.current &&
      frontendDeploy.status === "success"
    ) {
      window.location.reload();
    }
  }, [deploys, reloadOnFrontendSha]);

  const pollServices = useCallback(() => {
    const backendStart = performance.now();
    fetch(`${API_URL}/health`)
      .then((res) => {
        const ms = Math.round(performance.now() - backendStart);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        updateCheck(0, { status: "ok", detail: "OK", responseTime: ms });
      })
      .catch((err: unknown) => {
        updateCheck(0, {
          status: "error",
          detail:
            err instanceof Error
              ? err.message
              : "Backend unreachable — check your network.",
        });
      });

    try {
      const wsUrl = `${SPACETIMEDB_HOST}/v1/database/${SPACETIMEDB_DB}/subscribe`;
      const ws = new WebSocket(wsUrl, ["v1.bsatn.spacetimedb"]);
      const stdbStart = performance.now();
      const timeout = setTimeout(() => {
        ws.close();
        updateCheck(1, { status: "error", detail: "Timeout" });
      }, 5000);
      ws.onopen = () => {
        const ms = Math.round(performance.now() - stdbStart);
        clearTimeout(timeout);
        ws.close();
        updateCheck(1, { status: "ok", detail: "Connected", responseTime: ms });
      };
      ws.onerror = () => {
        clearTimeout(timeout);
        updateCheck(1, { status: "error", detail: "Connection failed" });
      };
    } catch {
      updateCheck(1, { status: "error", detail: "WebSocket unavailable" });
    }
  }, []);

  const pollDeploys = useCallback(() => {
    fetch(`${API_URL}/deploy-status`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ deploys: DeployInfo[] }>;
      })
      .then((data) => {
        const list = data.deploys ?? [];
        list.forEach((d, i) => {
          updateDeploy(i, {
            repo: d.repo,
            status: (d.status as DeployInfo["status"]) || "unknown",
            sha: d.sha?.slice(0, 7) ?? "",
            time: d.time ?? "",
            url: d.url ?? "",
          });
        });
      })
      .catch(() => {
        REPO_NAMES.forEach((_, i) => updateDeploy(i, { status: "unknown" }));
      });
  }, []);

  const [polling, setPolling] = useState(false);
  const [progress, setProgress] = useState(1);
  const nextPollRef = useRef(0);

  const pollAll = useCallback(() => {
    setPolling(true);
    pollServices();
    pollDeploys();
    nextPollRef.current = performance.now() + POLL_INTERVAL;
    setTimeout(() => {
      setPolling(false);
      setProgress(1);
    }, SPINNER_MS);
  }, [pollServices, pollDeploys]);

  useEffect(() => {
    pollAll();
    let id = window.setInterval(pollAll, POLL_INTERVAL);
    const onVis = () => {
      clearInterval(id);
      if (!document.hidden) {
        pollAll();
        id = window.setInterval(pollAll, POLL_INTERVAL);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [pollAll]);

  useEffect(() => {
    if (polling) return;
    const drainEnd = nextPollRef.current;
    const drainStart = performance.now();
    const drainMs = drainEnd - drainStart;
    if (drainMs <= 0) return;
    const id = setInterval(() => {
      const remaining = drainEnd - performance.now();
      setProgress(Math.max(0, remaining / drainMs));
    }, 50);
    return () => clearInterval(id);
  }, [polling]);

  return (
    <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
      <div className={cardClass} data-testid="services-card">
        <div className="px-5 pt-4 pb-2">
          <h2 className={dimClass}>Services</h2>
        </div>
        {checks.map((check, i) => (
          <div
            key={check.label}
            className={`flex items-center justify-between px-5 py-2.5 ${
              i < checks.length - 1 ? "border-b border-edge" : ""
            }`}
            data-testid={`service-${check.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className={labelClass}>{check.label}</span>
            <div className="flex items-center gap-2">
              {check.responseTime !== undefined && (
                <span className={monoClass}>{check.responseTime}ms</span>
              )}
              <span
                className={`text-xs font-medium ${statusColor(check.status)}`}
              >
                {check.detail}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={cardClass} data-testid="deploys-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className={dimClass}>Deploys</h2>
          {polling ? (
            <Loader2 size={14} className="text-muted animate-spin" />
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              className="text-muted"
            >
              <circle
                cx="7"
                cy="7"
                r="5.5"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.15"
                strokeWidth="1.5"
              />
              <circle
                cx="7"
                cy="7"
                r="5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray={`${2 * Math.PI * 5.5}`}
                strokeDashoffset={`${-2 * Math.PI * 5.5 * (1 - progress)}`}
                strokeLinecap="round"
                transform="rotate(-90 7 7)"
                style={{ transition: "stroke-dashoffset 100ms linear" }}
              />
            </svg>
          )}
        </div>
        {deploys.map((deploy, i) => (
          <div
            key={deploy.repo}
            className={`flex items-center justify-between px-5 py-2.5 ${
              i < deploys.length - 1 ? "border-b border-edge" : ""
            }`}
            data-testid={`deploy-${deploy.repo.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex flex-col gap-0.5">
              <span className={labelClass}>{deploy.repo}</span>
              <span className={monoClass}>
                {deploy.sha || "—"}
                {deploy.time ? ` · ${timeAgo(deploy.time)}` : ""}
              </span>
            </div>
            <a
              href={deploy.url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs font-medium ${statusColor(deploy.status)} ${
                deploy.url ? "hover:underline cursor-pointer" : "cursor-default"
              }`}
            >
              {deploy.status === "checking"
                ? "Checking…"
                : deploy.status === "success"
                  ? "Deployed"
                  : deploy.status === "in_progress"
                    ? "Deploying…"
                    : deploy.status === "failure"
                      ? "Failed"
                      : "Unknown"}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
